/**
 * SBV1 금고 blob 포맷 코덱 (v2) — **읽기 전용** (PR 3)
 *
 * 계약은 `backend/docs/design/vault_v2_format.md` 가 정본이다. 이 파일은 그 문서의
 * §2 레이아웃 · §4 키 재료 변환 · §5 IV · §6 AAD · §7 판별 · §8.0 헤더 검증을 구현한다.
 * 쓰기(encrypt)는 PR 6 에서 추가한다 — 그때까지 이 모듈에 암호화 경로를 두지 않는다.
 *
 * 🛑 이 파일은 `pUtils` 를 import 하지 않는 leaf 모듈이다 (§11). `pUtils` 가 이 모듈을
 *    import 하므로 반대 방향은 순환 참조다.
 *
 * 4개 구현체(웹·익스텐션·gate·백엔드)가 `testVectors.json` 을 상호운용 기준으로 삼는다.
 * 벡터를 고치면 4개 CI 를 모두 재검증해야 한다(§9.3).
 */
import forge from 'node-forge'

// 키 재료의 latin1 변환은 §4 가 지정한 기준 구현을 **재사용**한다. 복사하면 두 사본이
// 갈리고 그 순간 §4 규칙이 무의미해진다.
import { passwordToBytes } from '../attachmentV2/format'

const MAGIC = [0x53, 0x42, 0x56, 0x31] // "SBV1"
const HEADER_SIZE = 11 // MAGIC(4) + version(1) + cipher(1) + kdf(1) + iter(4)
const SALT_SIZE = 16
const IV_SIZE = 12
const TAG_SIZE = 16
const KEY_SIZE = 32 // AES-256

const KNOWN_VERSIONS = [0x01]
const KNOWN_CIPHERS = [0x01] // AES-256-GCM
const KDF_NONE = 0x00
const KDF_PBKDF2 = 0x01
const KNOWN_KDFS = [KDF_NONE, KDF_PBKDF2]

// §8.0 — `iter` 는 태그 검증보다 먼저 리더의 연산량을 정하므로 사용 전에 검증한다.
// 값은 §3 의 실사용 반복 횟수 + D4 의 신규 쓰기값(600,000).
const ALLOWED_ITERATIONS = [10000, 100000, 600000]

export class VaultFormatError extends Error {
  constructor(code, message) {
    super(message)
    this.name = 'VaultFormatError'
    // 문자열 코드. 서버 에러는 `code: number`(`api/Workspace.ts:13`)이므로 소비처는
    // `typeof e.code === 'number'` 로 둘을 구분한다.
    this.code = code
  }
}

const toKeyBytes = (byteString) => {
  try {
    return passwordToBytes(byteString)
  } catch (e) {
    // 기준 구현은 `ChunkFormatError('BAD_KEY_MATERIAL')` 을 던진다. 이 포맷의 에러 분류는
    // §8.2 이므로 코드를 옮긴다 — 두 포맷이 같은 코드 이름을 공유하지 않게 한다.
    throw new VaultFormatError(
      'KEY_MATERIAL_INVALID',
      'key material must be a byte string (charCode <= 0xFF)',
    )
  }
}

const bytesToByteString = (bytes) => {
  let out = ''
  bytes.forEach((b) => {
    out += String.fromCharCode(b)
  })
  return out
}

/**
 * blob(바이트 문자열) → bytes.
 *
 * 🛑 키 재료(`toKeyBytes`)와 달리 `0xFF` 초과에서 던지지 않는다. blob 에 그런 문자가 있으면
 *    애초에 v2 blob 이 아니므로 매직 검사가 걸러 v1 경로로 내려간다 — 즉 **기존 v1 동작이
 *    그대로 보존된다.** 여기서 던지면 이 PR(읽기 전용)이 v1 경로의 계약을 바꾸게 된다.
 *    키 재료는 반대다: 조용히 잘리면 디버깅 불가한 키 불일치가 되므로 명시 실패시킨다(§4).
 */
const byteStringToBytes = (s) => {
  const out = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    out[i] = s.charCodeAt(i) & 0xff
  }
  return out
}

/** `readU32BE` 의 역방향. 인코더가 헤더의 `iter` 를 채울 때 쓴다. */
const writeU32BE = (bytes, offset, value) => {
  /* eslint-disable no-param-reassign, no-bitwise */
  bytes[offset] = (value >>> 24) & 0xff
  bytes[offset + 1] = (value >>> 16) & 0xff
  bytes[offset + 2] = (value >>> 8) & 0xff
  bytes[offset + 3] = value & 0xff
  /* eslint-enable no-param-reassign, no-bitwise */
}

/**
 * 운영 경로의 salt·IV 원천.
 *
 * `forge.random` 을 쓰는 이유: 이 저장소의 기존 salt 생성이 그것이고(`pUtils.js` 의
 * `encryptAESByKey`), **환경 전역에 의존하지 않는다**. `crypto.getRandomValues` 로 하면
 * jsdom 테스트에서 polyfill 이 필요해지는데, 그것은 인코더가 아니라 하네스의 문제를
 * 인코더 쪽으로 옮기는 것이다. (`derivePbkdf2` 가 `crypto.subtle` 을 쓰는 것은 forge 의
 * PBKDF2 가 동기라 10만 회에서 UI 를 막기 때문이고, 난수는 그 사정이 없다.)
 */
const randomByteString = (length) => forge.random.getBytesSync(length)

const readU32BE = (bytes, offset) =>
  // eslint-disable-next-line no-bitwise
  ((bytes[offset] << 24) |
    // eslint-disable-next-line no-bitwise
    (bytes[offset + 1] << 16) |
    // eslint-disable-next-line no-bitwise
    (bytes[offset + 2] << 8) |
    bytes[offset + 3]) >>>
  0

const hasMagic = (bytes) => bytes.length >= HEADER_SIZE && MAGIC.every((b, i) => bytes[i] === b)

// 하한이지 등호가 아니다 — 헤더에 평문 길이 필드가 없어 정확한 길이를 검사할 수 없다.
// 그래서 뒤가 잘린 blob 은 이 검사가 아니라 GCM 태그가 잡는다 (§7 minLength 주석).
const minLength = (kdf) => HEADER_SIZE + (kdf === KDF_NONE ? 0 : SALT_SIZE) + IV_SIZE + TAG_SIZE

/**
 * §8.0 헤더 검증. 값을 **사용하기 전에** 부른다. 이 실패는 v1 으로 폴백하지 않고 전파한다 —
 * 매직·version·cipher·kdf·구조가 모두 맞은 blob 은 "구포맷"이 아니라 "잘못된 v2" 다.
 */
const assertHeaderAllowed = (kdf, iterations, keyBytesLength) => {
  if (kdf === KDF_NONE) {
    if (iterations !== 0) {
      throw new VaultFormatError('KDF_PARAMS_INVALID', 'kdf=none requires iter=0')
    }
    // 16·24바이트 키를 node-forge 와 python cryptography 가 **둘 다 에러 없이** 받아
    // AES-128/192 로 동작한다(2026-08-24 실측). 조용한 강등을 여기서 막는다.
    if (keyBytesLength !== KEY_SIZE) {
      throw new VaultFormatError(
        'KEY_MATERIAL_INVALID',
        `kdf=none requires exactly ${KEY_SIZE}-byte key material`,
      )
    }
    return
  }
  // kdf=PBKDF2 는 길이 검증이 원리적으로 불가능하다 — PBKDF2 가 입력 길이와 무관하게
  // 32바이트를 내므로 "사용자가 짧게 입력한 것"과 "버그가 잘랐다"를 구분할 수 없다.
  if (!ALLOWED_ITERATIONS.includes(iterations)) {
    throw new VaultFormatError('KDF_PARAMS_INVALID', 'iter is outside the allowed set')
  }
}

// 🛑 `window.crypto` 가 아니라 접두사 없는 `crypto` 를 쓴다. 익스텐션의 MV3 서비스 워커에는
// `window` 가 없어 그 형태로는 포팅이 불가능하다 — 선례가 이미 그렇다
// (`extension/src/utils/crypto.ts:40,48` · `gate/crypto.mjs:13,15`).
// `pUtils.js:23,30` 은 `window.crypto` 를 쓰지만 그쪽은 웹 전용 코드라 문제되지 않는다.
const derivePbkdf2 = async (keyBytes, saltBytes, iterations) => {
  const material = await crypto.subtle.importKey('raw', keyBytes, 'PBKDF2', false, ['deriveBits'])
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: saltBytes, iterations, hash: 'SHA-256' },
    material,
    KEY_SIZE * 8,
  )
  return new Uint8Array(bits)
}

/**
 * 평문을 SBV1(v2) blob 으로 만든다 — `decryptV2` 의 역방향.
 *
 * **판독기와 같은 파일에 두는 이유**: 헤더 조립·파생·검증을 같은 헬퍼로 하면 둘이 갈라질 수
 * 없다. 백엔드도 같은 구조다(`common/vault_v2.py` 가 encode·decode 를 함께 갖는다).
 *
 * 🛑 **파라미터 오류는 강등하지 않고 예외로 올린다.** 포맷 *판별* 실패는 구포맷으로 강등하는
 *    것이 맞지만(`vaultWriteFormat.js` 의 갈림길), 인코더 파라미터 오류를 강등하면 헤더에 못
 *    넣는 값으로 blob 을 만들어 **판독기가 못 읽는 데이터**가 조용히 저장된다.
 *
 * `salt`·`iv` 는 **교차 벡터 재현용 테스트 주입구**다. 운영 경로는 둘 다 생략해
 * `randomByteString`(forge) 이 만들게 한다 — 고정 IV 는 GCM 에서 nonce 재사용이고, 같은 키로
 * 두 번 쓰면 인증이 무너진다.
 *
 * @param {string} plaintext 바이트 문자열
 * @param {string} keyMaterial 바이트 문자열 (§4 — 각 문자가 0x00~0xFF)
 * @param {{kdf: number, iterations: number, salt?: string|null, iv?: string|null}} options
 * @returns {Promise<string>} blob (바이트 문자열) — base64 는 호출부가 씌운다
 */
export const encryptVaultBlobV2 = async (
  plaintext,
  keyMaterial,
  { kdf, iterations, salt = null, iv = null } = {},
) => {
  const keyBytes = toKeyBytes(keyMaterial)
  assertHeaderAllowed(kdf, iterations, keyBytes.length)

  const header = new Uint8Array(HEADER_SIZE)
  MAGIC.forEach((b, i) => {
    header[i] = b
  })
  const [version] = KNOWN_VERSIONS
  const [cipherId] = KNOWN_CIPHERS
  header[4] = version
  header[5] = cipherId
  header[6] = kdf
  writeU32BE(header, 7, iterations)

  let saltBytes = null
  let aesKey = keyBytes
  if (kdf !== KDF_NONE) {
    saltBytes = byteStringToBytes(salt === null ? randomByteString(SALT_SIZE) : salt)
    if (saltBytes.length !== SALT_SIZE) {
      throw new VaultFormatError('KDF_PARAMS_INVALID', `salt must be exactly ${SALT_SIZE} bytes`)
    }
    aesKey = await derivePbkdf2(keyBytes, saltBytes, iterations)
  }

  const ivBytes = byteStringToBytes(iv === null ? randomByteString(IV_SIZE) : iv)
  if (ivBytes.length !== IV_SIZE) {
    throw new VaultFormatError('KDF_PARAMS_INVALID', `iv must be exactly ${IV_SIZE} bytes`)
  }

  const cipher = forge.cipher.createCipher('AES-GCM', bytesToByteString(aesKey))
  cipher.start({
    iv: forge.util.createBuffer(bytesToByteString(ivBytes)),
    tagLength: TAG_SIZE * 8,
    // AAD = 헤더 11바이트 전체 (§6). salt·IV 는 포함하지 않는다 — decryptV2 와 같아야 한다.
    additionalData: bytesToByteString(header),
  })
  cipher.update(forge.util.createBuffer(plaintext))
  cipher.finish()

  return (
    bytesToByteString(header) +
    (saltBytes ? bytesToByteString(saltBytes) : '') +
    bytesToByteString(ivBytes) +
    cipher.output.getBytes() +
    cipher.mode.tag.getBytes()
  )
}

/**
 * 헤더 파싱. `header` 가 있으면 v2 확정이고, 없으면 `reason` 이 **왜 아닌지**를 담는다.
 *
 * `reason` 을 구분하는 이유: 2단계 판정에서 v1 도 실패했을 때 낼 에러 코드가 갈린다.
 *  - `NO_MAGIC` / `UNKNOWN_FIELDS` → `UNSUPPORTED_VERSION` (미래 포맷일 수 있다)
 *  - `TOO_SHORT` → `FORMAT_INVALID` (필드는 다 아는데 길이가 부족 = 손상. §8.2 "길이 부족")
 *
 * 판정만 갈리고 **2단계 자체는 그대로** 돈다 — 우연 일치한 v1 구제를 깨지 않는다.
 */
const parseHeader = (bytes) => {
  if (!hasMagic(bytes)) return { header: null, reason: 'NO_MAGIC' }
  const kdf = bytes[6]
  if (
    !KNOWN_VERSIONS.includes(bytes[4]) ||
    !KNOWN_CIPHERS.includes(bytes[5]) ||
    !KNOWN_KDFS.includes(kdf)
  ) {
    return { header: null, reason: 'UNKNOWN_FIELDS' }
  }
  if (bytes.length < minLength(kdf)) {
    return { header: null, reason: 'TOO_SHORT' }
  }
  return { header: { kdf, iterations: readU32BE(bytes, 7) }, reason: null }
}

const decryptV2 = async (bytes, header, keyMaterial) => {
  const keyBytes = toKeyBytes(keyMaterial)
  assertHeaderAllowed(header.kdf, header.iterations, keyBytes.length)

  let offset = HEADER_SIZE
  let saltBytes = null
  if (header.kdf !== KDF_NONE) {
    saltBytes = bytes.subarray(offset, offset + SALT_SIZE)
    offset += SALT_SIZE
  }
  const ivBytes = bytes.subarray(offset, offset + IV_SIZE)
  offset += IV_SIZE
  const cipherBytes = bytes.subarray(offset, bytes.length - TAG_SIZE)
  const tagBytes = bytes.subarray(bytes.length - TAG_SIZE)

  const aesKey =
    header.kdf === KDF_NONE ? keyBytes : await derivePbkdf2(keyBytes, saltBytes, header.iterations)

  const decipher = forge.cipher.createDecipher('AES-GCM', bytesToByteString(aesKey))
  decipher.start({
    iv: forge.util.createBuffer(bytesToByteString(ivBytes)),
    tagLength: TAG_SIZE * 8,
    tag: forge.util.createBuffer(bytesToByteString(tagBytes)),
    // AAD = 헤더 11바이트 전체(§6). salt·IV 는 포함하지 않는다.
    additionalData: bytesToByteString(bytes.subarray(0, HEADER_SIZE)),
  })
  decipher.update(forge.util.createBuffer(bytesToByteString(cipherBytes)))

  // finish()=false 는 태그 불일치다. 반환값을 버리면 쓰레기 평문이 하류로 흘러간다
  // (PR 0 에서 4개 구현체에 통일한 가드와 같은 이유).
  if (!decipher.finish()) {
    // 🛑 메시지에 'decrypt' 를 반드시 포함시킨다 — `extension/.../resourceService.ts:166-170`
    // 이 stale user_key 자동 갱신을 `e.message.includes('decrypt'|'padding')` 로 발동한다.
    // v1 메시지들과 같은 접두사를 쓴다(`decryptAES:85` · `gate/crypto.mjs:32`).
    throw new VaultFormatError('TAG_MISMATCH', 'AES-GCM decrypt failed: tag verification failed')
  }
  return decipher.output.getBytes()
}

/**
 * 이 blob 이 **v2 인가** (PR 7-a Task A1).
 *
 * 🛑 **정방향 스윕이 이 함수를 필요로 하는 이유.** 역방향(`reverseSweep.js:50-52`)은
 * `decryptVaultBlob` 을 직접 부르며 v1 thunk 가 호출됐는지로 판별한다. 정방향은
 * `rsKeyObj.decrypt` 를 쓰는데 그 안의 `decryptAESByKey`(`pUtils.js:113`)가 thunk 를
 * **자기가 만들어 넘기므로** 바깥에서 관측할 수 없다. 접두사 검사를 호출부에 복제하면
 * 매직 지식의 세 번째 사본이 되므로, 이미 그 지식을 가진 `parseHeader` 를 그대로 재사용한다.
 *
 * `header != null` 일 때만 v2 로 확정한다 — 매직은 맞지만 버전·필드·길이가 이상하면
 * `header: null` 이고, 그런 값은 **변환 대상으로 남겨** 복호 단계에서
 * `UNSUPPORTED_VERSION`/`FORMAT_INVALID` 로 갈리게 한다.
 *
 * @param {Uint8Array|ArrayLike<number>|string} blob 바이트 배열 또는 바이트 문자열
 */
export const isVaultV2 = (blob) => {
  const bytes = typeof blob === 'string' ? byteStringToBytes(blob) : new Uint8Array(blob)
  return parseHeader(bytes).header != null
}

/**
 * v1/v2 를 판별해 복호한다 (§7 2단계 판정).
 *
 * **2단계 판정을 이 함수 하나에 둔다** — 4개 구현체의 호출부에 복제하면 그 부분이 갈린다.
 * 각 구현체는 자기 v1 복호기를 `decryptV1Thunk` 로 주입한다.
 *
 * @param {Uint8Array|ArrayLike<number>} blobBytes
 * @param {string} keyMaterial 바이트 문자열 (§4 — 각 문자가 0x00~0xFF)
 * @param {() => (string|Promise<string>)} decryptV1Thunk 구포맷 복호기. 인자는 호출자가 닫아둔다.
 * @returns {Promise<string>} 평문 바이트 문자열
 */
export const decryptVaultBlob = async (blob, keyMaterial, decryptV1Thunk) => {
  // 웹·익스텐션·gate 는 blob 을 **바이트 문자열**로 들고 있고(forge 계열) 백엔드는 bytes 다.
  // 양쪽을 여기서 받아 호출부 4곳에 변환 코드를 복제하지 않는다.
  const bytes = typeof blob === 'string' ? byteStringToBytes(blob) : new Uint8Array(blob)
  const { header, reason } = parseHeader(bytes)

  if (header) {
    // v2 로 확정. 여기서의 실패는 전파한다 (구포맷으로 되돌리지 않는다).
    return decryptV2(bytes, header, keyMaterial)
  }

  // v2 가 아니다. 매직까지는 맞았는지가 실패 시 에러를 가른다:
  //  - 매직도 아니면 그냥 구포맷 → v1 실패를 그대로 전파
  //  - 매직은 맞는데 v2 가 아니면 "우연 일치한 v1" 이거나 "손상/미래 포맷" →
  //    v1 이 열리면 전자(구제), 안 열리면 reason 에 따라 코드를 나눈다
  try {
    return await decryptV1Thunk()
  } catch (v1Error) {
    if (reason === 'TOO_SHORT') {
      // 필드는 다 아는데 길이가 부족하다 = v2 가 잘렸다. "미지의 버전" 이 아니라 손상이다.
      throw new VaultFormatError('FORMAT_INVALID', 'SBV1 blob is shorter than the fixed minimum')
    }
    if (reason === 'UNKNOWN_FIELDS') {
      throw new VaultFormatError(
        'UNSUPPORTED_VERSION',
        'SBV1 magic with unknown version/cipher/kdf and the legacy path failed too',
      )
    }
    throw v1Error
  }
}

export const VAULT_V2_CONSTANTS = {
  MAGIC,
  HEADER_SIZE,
  SALT_SIZE,
  IV_SIZE,
  TAG_SIZE,
  KEY_SIZE,
  ALLOWED_ITERATIONS,
  KDF_NONE,
  KDF_PBKDF2,
  minLength,
}
